import {
  AlertTriangle,
  Bell,
  Building2,
  Check,
  CheckCheck,
  DollarSign,
  Shield,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { services } from '../../services';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';

interface Notification {
  id: string;
  type:
    | 'kyc'
    | 'distribution'
    | 'user'
    | 'asset'
    | 'alert'
    | 'yield'
    | 'transaction'
    | 'compliance';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high';
}

const getIcon = (type: Notification['type']) => {
  switch (type) {
    case 'kyc':
      return <Shield className='w-4 h-4' />;
    case 'distribution':
      return <DollarSign className='w-4 h-4' />;
    case 'user':
      return <Users className='w-4 h-4' />;
    case 'asset':
      return <Building2 className='w-4 h-4' />;
    case 'alert':
      return <AlertTriangle className='w-4 h-4' />;
    case 'yield':
      return <TrendingUp className='w-4 h-4' />;
    case 'transaction':
      return <DollarSign className='w-4 h-4' />;
    case 'compliance':
      return <Shield className='w-4 h-4' />;
  }
};

const getIconStyles = (type: Notification['type'], priority: Notification['priority']) => {
  if (priority === 'high') return 'bg-destructive/20 text-destructive';
  switch (type) {
    case 'kyc':
      return 'bg-warning/20 text-warning';
    case 'distribution':
      return 'bg-success/20 text-success';
    case 'user':
      return 'bg-info/20 text-info';
    case 'asset':
      return 'bg-primary/20 text-primary';
    case 'alert':
      return 'bg-destructive/20 text-destructive';
    case 'yield':
      return 'bg-success/20 text-success';
    case 'transaction':
      return 'bg-success/20 text-success';
    case 'compliance':
      return 'bg-warning/20 text-warning';
  }
};

const formatTimeAgo = (date: Date) => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export function NotificationsPanel() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: Array<{
          _id?: string;
          id?: string;
          type: string;
          title: string;
          message: string;
          createdAt?: string;
          timestamp?: string;
          read?: boolean;
          notificationStatus?: string;
          priority?: string;
        }>;
      }>('/admin/notifications/list');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const formattedNotifications = response.data.data.map((notif) => ({
          id: notif._id || notif.id || '',
          type: (notif.type || 'alert') as Notification['type'],
          title: notif.title || 'Notification',
          message: notif.message || '',
          timestamp: new Date(notif.createdAt || notif.timestamp || Date.now()),
          read: notif.read || notif.notificationStatus === 'read',
          priority: (notif.priority || 'medium') as Notification['priority'],
        }));
        setNotifications(formattedNotifications);
      } else if (response.status === 401) {
        console.error('Authentication required for notifications');
      } else if (response.status === 403) {
        console.error('Admin access required for notifications');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for notifications');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for notifications');
      } else {
        console.error('Error fetching notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await services.put<{ status: boolean; message?: string }>(
        '/admin/notifications/updateStatus',
        { notificationId: id }
      );
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required to mark notification as read');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required to mark notification as read');
      } else {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await services.put<{ status: boolean; message?: string }>(
        '/admin/notifications/markAllRead',
        {}
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required to mark all notifications as read');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required to mark all notifications as read');
      } else {
        console.error('Error marking all notifications as read:', error);
      }
    }
  };

  const removeNotification = async (id: string) => {
    try {
      await services.delete<{ status: boolean; message?: string }>(`/admin/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required to remove notification');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required to remove notification');
      } else {
        console.error('Error removing notification:', error);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='ghost' size='icon' className='relative'>
          <Bell className='w-5 h-5' />
          {unreadCount > 0 && (
            <span className='absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center animate-pulse'>
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-96 p-0' align='end'>
        <div className='flex items-center justify-between p-4 border-b border-border/50'>
          <div className='flex items-center gap-2'>
            <h3 className='font-semibold'>Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant='secondary' className='text-xs'>
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant='ghost' size='sm' onClick={markAllAsRead}>
              <CheckCheck className='w-4 h-4 mr-1' />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className='h-[400px]'>
          <div className='divide-y divide-border/50'>
            {loading ? (
              <div className='p-8 text-center text-muted-foreground'>
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className='p-8 text-center text-muted-foreground'>
                <Bell className='w-10 h-10 mx-auto mb-3 opacity-50' />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted/30 transition-colors group ${
                    !notification.read ? 'bg-muted/20' : ''
                  }`}
                >
                  <div className='flex gap-3'>
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${getIconStyles(
                        notification.type,
                        notification.priority
                      )}`}
                    >
                      {getIcon(notification.type)}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-start justify-between gap-2'>
                        <h4
                          className={`text-sm font-medium ${
                            !notification.read ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          {notification.title}
                        </h4>
                        <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                          {!notification.read && (
                            <Button
                              variant='ghost'
                              size='icon'
                              className='h-6 w-6'
                              onClick={() => markAsRead(notification.id)}
                            >
                              <Check className='w-3 h-3' />
                            </Button>
                          )}
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-6 w-6'
                            onClick={() => removeNotification(notification.id)}
                          >
                            <X className='w-3 h-3' />
                          </Button>
                        </div>
                      </div>
                      <p className='text-sm text-muted-foreground mt-0.5'>{notification.message}</p>
                      <p className='text-xs text-muted-foreground mt-1.5'>
                        {formatTimeAgo(notification.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
